import * as THREE from 'three';
import { SplatMesh, SplatLoader, PackedSplats } from '@sparkjsdev/spark';

/**
 * Gaussian Splatting renderer using Spark for 3DGS visualization
 */
export class GaussianSplattingRenderer {
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private splats: any[] = []; // Using any[] to avoid type issues with Spark
  private currentSplat: SplatMesh | null = null;
  private splatCount: number = 50000;
  private isLoading: boolean = false;

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;
  }

  private async loadSplatPromise(
    url: string,
    onProgress?: (perc: number) => void
  ): Promise<PackedSplats> {
    return new Promise((resolve, reject) => {
      const loader = new SplatLoader();
      loader.load(
        url,
        (splat) => resolve(splat),
        (ev) => {
          if (onProgress) onProgress(ev.loaded / ev.total);
        },
        (error) => reject(error)
      );
    });
  }

  /**
   * Load a 3DGS model from a URL
   * Supports .ply, .spz, .splat, .ksplat, .sog formats
   */
  public async loadFromURL(url: string, onProgress?: (perc: number) => void): Promise<void> {
    if (this.isLoading) {
      throw new Error('Already loading a model');
    }

    this.isLoading = true;

    try {
      // Clear existing splats
      this.clear();

      console.log(`Loading 3DGS model from: ${url}`);

      const splats = await this.loadSplatPromise(url, onProgress);

      // Create SplatMesh with URL
      const splatMesh = new SplatMesh({
        packedSplats: splats,
        maxSplats: this.splatCount || 50000,
      });

      // Position the splat
      splatMesh.position.set(0, 0, 0);
      splatMesh.quaternion.set(0, 0, 0, 1);
      splatMesh.rotateX(Math.PI); // Fix upside-down orientation when source uses different up-axis
      this.currentSplat = splatMesh;

      // Add to scene
      this.scene.add(splatMesh);
      this.splats.push(splatMesh);

      console.log(`Successfully loaded 3DGS model from: ${url}`);
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      console.error('Failed to load 3DGS model:', error);
      throw error;
    }
  }

  /**
   * Load demo model from example URL
   */
  public async loadDemo(onProgress?: (perc: number) => void): Promise<void> {
    const demoURL = `/assets/butterfly.spz`;
    return this.loadFromURL(demoURL, onProgress);
  }

  /**
   * Get the count of splats in the scene
   */
  public getSplatCount(): number {
    // Return actual loaded splat count from currentSplat if available
    if (this.currentSplat) {
      return this.currentSplat.packedSplats.numSplats;
    }
    return 0;
  }

  public setSplatCount(count: number): void {
    this.splatCount = count;

    // Update maxSplats on existing SplatMesh instances
    this.splats.forEach((splat) => {
      if (splat.maxSplats !== undefined) {
        splat.maxSplats = count;
        console.log(`Updated maxSplats to ${count} for existing SplatMesh`);
      }
    });
  }

  /**
   * Get metadata about the currently loaded splat model
   */
  public getSplatMetadata(): any {
    if (!this.currentSplat) return null;
    const packedSplats = this.currentSplat.packedSplats;
    return {
      numSplats: packedSplats.numSplats,
      maxSplats: packedSplats.maxSplats,
    };
  }

  /**
   * Update splat properties (e.g., opacity, scale)
   */
  public updateSplats(properties: { opacity?: number; scale?: number }): void {
    this.splats.forEach((splat) => {
      if (properties.scale !== undefined) {
        splat.scale.set(properties.scale, properties.scale, properties.scale);
      }
    });
  }

  /**
   * Rotate the model
   */
  public rotate(x: number, y: number, z: number): void {
    this.splats.forEach((splat) => {
      splat.rotation.x += x;
      splat.rotation.y += y;
      splat.rotation.z += z;
    });
  }

  /**
   * Scale the model
   */
  public scale(factor: number): void {
    this.splats.forEach((splat) => {
      splat.scale.multiplyScalar(factor);
    });
  }

  /**
   * Clear all splats from the scene
   */
  public clear(): void {
    this.splats.forEach((splat) => {
      this.scene.remove(splat);
      splat.dispose?.();
    });
    this.splats = [];
    this.splatCount = 0;
  }

  /**
   * Dispose resources
   */
  public dispose(): void {
    this.clear();
  }
}
